// Social media feeds (task 41, PLAN §14 M4): the connector framework. Each platform has a connector with one
// interface (read an account's posts since a cursor); a feed follows one account on one platform and stores each
// new post as a document (source channel `social`) that goes through the same IngestWorkflow as an upload.
//
// In this build every platform connector is a **stub**: it declares the API credentials it will need and stays
// unavailable, so feeds on it can be set up but not switched on. Live connectors are out of the current scope
// (PLAN §1). A development-only test platform exercises the whole path (feed → posts → documents) in tests.

import { SOCIAL_PLATFORMS } from './schema/values';
import { SCRAPER_SCHEDULES, type ScraperSchedule } from './scrapers';
import type { ReviewMode, Sensitivity } from './uploads';

/** Platforms a feed can follow. `test` exists only in local development (its posts are set by tests). */
export const SOCIAL_FEED_PLATFORMS = [
  'x',
  'facebook',
  'instagram',
  'youtube',
  'linkedin',
  'tiktok',
  'telegram',
  'bluesky',
  'threads',
  'mastodon',
  'test',
] as const;
export type SocialFeedPlatform = (typeof SOCIAL_FEED_PLATFORMS)[number];

/** Feeds reuse the website monitors' schedules (every hour … every week, or by hand). */
export const SOCIAL_FEED_SCHEDULES = SCRAPER_SCHEDULES;
export type SocialFeedSchedule = ScraperSchedule;

export interface SocialCredentialDef {
  /** The Worker secret's name (set by an admin with `wrangler secret put`); its value is never shown or returned. */
  key: string;
  label: string;
}

export interface SocialPlatformDef {
  label: string;
  /** What a feed follows on this platform, e.g. "account", "page", "channel". */
  account: string;
  handleHint: string;
  handlePattern: RegExp;
  /** Hosts a pasted profile address may be on. */
  hosts: readonly string[];
  profileUrl: (handle: string) => string;
  postUrl?: (handle: string, postId: string) => string;
  /** The API the live connector will use, and the credentials it needs. */
  api: string;
  credentials: readonly SocialCredentialDef[];
  /** Access that has to be applied for, beyond the credentials themselves. */
  approval?: string;
  devOnly?: boolean;
}

const hostsOf = (p: keyof typeof SOCIAL_PLATFORMS) => SOCIAL_PLATFORMS[p].hosts as readonly string[];

export const SOCIAL_PLATFORM_DEFS: Record<SocialFeedPlatform, SocialPlatformDef> = {
  x: {
    label: SOCIAL_PLATFORMS.x.label,
    account: 'account',
    handleHint: '@name or https://x.com/name',
    handlePattern: /^[A-Za-z0-9_]{1,15}$/,
    hosts: hostsOf('x'),
    profileUrl: h => `https://x.com/${h}`,
    postUrl: (h, id) => `https://x.com/${h}/status/${id}`,
    api: 'X API v2 (user timeline)',
    credentials: [{ key: 'SOCIAL_X_BEARER_TOKEN', label: 'X API bearer token' }],
    approval: 'A paid X API plan that includes reading timelines.',
  },
  facebook: {
    label: SOCIAL_PLATFORMS.facebook.label,
    account: 'page',
    handleHint: 'page name or https://facebook.com/pagename',
    handlePattern: /^[A-Za-z0-9.]{2,75}$/,
    hosts: hostsOf('facebook'),
    profileUrl: h => `https://www.facebook.com/${h}`,
    api: 'Meta Graph API (page posts)',
    credentials: [
      { key: 'SOCIAL_META_APP_ID', label: 'Meta app ID' },
      { key: 'SOCIAL_META_ACCESS_TOKEN', label: 'Meta access token' },
    ],
    approval: 'Meta’s Page Public Content Access feature, granted after app review.',
  },
  instagram: {
    label: SOCIAL_PLATFORMS.instagram.label,
    account: 'account',
    handleHint: '@name or https://instagram.com/name',
    handlePattern: /^[A-Za-z0-9._]{1,30}$/,
    hosts: hostsOf('instagram'),
    profileUrl: h => `https://www.instagram.com/${h}/`,
    api: 'Instagram Graph API (business discovery)',
    credentials: [
      { key: 'SOCIAL_META_ACCESS_TOKEN', label: 'Meta access token' },
      { key: 'SOCIAL_INSTAGRAM_ACCOUNT_ID', label: 'Your Instagram business account ID' },
    ],
    approval: 'Only professional (business or creator) accounts can be read, through your own business account.',
  },
  youtube: {
    label: SOCIAL_PLATFORMS.youtube.label,
    account: 'channel',
    handleHint: '@channel, a channel ID (UC…) or https://youtube.com/@channel',
    handlePattern: /^(?:[A-Za-z0-9._-]{3,30}|UC[A-Za-z0-9_-]{22})$/,
    hosts: hostsOf('youtube'),
    profileUrl: h => (/^UC[A-Za-z0-9_-]{22}$/.test(h) ? `https://www.youtube.com/channel/${h}` : `https://www.youtube.com/@${h}`),
    postUrl: (_h, id) => `https://www.youtube.com/watch?v=${id}`,
    api: 'YouTube Data API v3 (channel uploads)',
    credentials: [{ key: 'SOCIAL_YOUTUBE_API_KEY', label: 'YouTube Data API key' }],
  },
  linkedin: {
    label: SOCIAL_PLATFORMS.linkedin.label,
    account: 'company page',
    handleHint: 'company name in the address, or https://linkedin.com/company/name',
    handlePattern: /^[A-Za-z0-9-]{2,100}$/,
    hosts: hostsOf('linkedin'),
    profileUrl: h => `https://www.linkedin.com/company/${h}/`,
    api: 'LinkedIn Community Management API (organization posts)',
    credentials: [{ key: 'SOCIAL_LINKEDIN_ACCESS_TOKEN', label: 'LinkedIn access token' }],
    approval: 'LinkedIn’s Community Management API, granted after review; only company pages can be read.',
  },
  tiktok: {
    label: SOCIAL_PLATFORMS.tiktok.label,
    account: 'account',
    handleHint: '@name or https://tiktok.com/@name',
    handlePattern: /^[A-Za-z0-9._]{2,24}$/,
    hosts: hostsOf('tiktok'),
    profileUrl: h => `https://www.tiktok.com/@${h}`,
    postUrl: (h, id) => `https://www.tiktok.com/@${h}/video/${id}`,
    api: 'TikTok Research API (user videos)',
    credentials: [
      { key: 'SOCIAL_TIKTOK_CLIENT_KEY', label: 'TikTok client key' },
      { key: 'SOCIAL_TIKTOK_CLIENT_SECRET', label: 'TikTok client secret' },
    ],
    approval: 'TikTok grants the Research API to approved research organizations only.',
  },
  telegram: {
    label: SOCIAL_PLATFORMS.telegram.label,
    account: 'public channel',
    handleHint: '@channel or https://t.me/channel',
    handlePattern: /^[A-Za-z][A-Za-z0-9_]{3,31}$/,
    hosts: hostsOf('telegram'),
    profileUrl: h => `https://t.me/${h}`,
    postUrl: (h, id) => `https://t.me/${h}/${id}`,
    api: 'Telegram Bot API (channel posts; the bot must be added to the channel)',
    credentials: [{ key: 'SOCIAL_TELEGRAM_BOT_TOKEN', label: 'Telegram bot token' }],
  },
  bluesky: {
    label: SOCIAL_PLATFORMS.bluesky.label,
    account: 'account',
    handleHint: '@name.bsky.social or https://bsky.app/profile/name.bsky.social',
    handlePattern: /^(?=.{3,253}$)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/,
    hosts: hostsOf('bluesky'),
    profileUrl: h => `https://bsky.app/profile/${h}`,
    api: 'AT Protocol (author feed)',
    credentials: [
      { key: 'SOCIAL_BLUESKY_HANDLE', label: 'Bluesky account used to read' },
      { key: 'SOCIAL_BLUESKY_APP_PASSWORD', label: 'Bluesky app password' },
    ],
  },
  threads: {
    label: SOCIAL_PLATFORMS.threads.label,
    account: 'account',
    handleHint: '@name or https://threads.net/@name',
    handlePattern: /^[A-Za-z0-9._]{1,30}$/,
    hosts: hostsOf('threads'),
    profileUrl: h => `https://www.threads.net/@${h}`,
    api: 'Threads API (profile posts)',
    credentials: [{ key: 'SOCIAL_THREADS_ACCESS_TOKEN', label: 'Threads access token' }],
    approval: 'Meta’s threads_basic and threads_profile_discovery permissions, granted after app review.',
  },
  mastodon: {
    label: SOCIAL_PLATFORMS.mastodon.label,
    account: 'account',
    handleHint: '@name@server.social or https://server.social/@name',
    handlePattern: /^[A-Za-z0-9_]{1,30}@(?=.{3,253}$)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/,
    hosts: [],
    profileUrl: h => {
      const [user, server] = h.split('@');
      return `https://${server}/@${user}`;
    },
    api: 'Mastodon API (account statuses) on the account’s server',
    credentials: [{ key: 'SOCIAL_MASTODON_ACCESS_TOKEN', label: 'Mastodon access token' }],
  },
  test: {
    label: 'Test feed (development only)',
    account: 'account',
    handleHint: 'any name',
    handlePattern: /^[A-Za-z0-9_-]{1,40}$/,
    hosts: [],
    profileUrl: h => `https://social.test/${h}`,
    postUrl: (h, id) => `https://social.test/${h}/${id}`,
    api: 'Posts set by tests in local development',
    credentials: [],
    devOnly: true,
  },
};

/**
 * Where a platform's connector stands:
 * - `stub`: the connector is not built in this release; feeds can be set up but not switched on.
 * - `needs_credentials`: the connector is built, but its API credentials are not set.
 * - `ready`: feeds can be switched on and checked.
 */
export const SOCIAL_CONNECTOR_STATES = ['stub', 'needs_credentials', 'ready'] as const;
export type SocialConnectorState = (typeof SOCIAL_CONNECTOR_STATES)[number];

export const SOCIAL_CONNECTOR_STATE_LABELS: Record<SocialConnectorState, string> = {
  stub: 'Not available yet',
  needs_credentials: 'Needs API credentials',
  ready: 'Ready',
};

export interface SocialConnectorSummary {
  platform: SocialFeedPlatform;
  label: string;
  account: string;
  handleHint: string;
  api: string;
  approval: string | null;
  state: SocialConnectorState;
  /** Why the connector can't run feeds yet, in words (null when ready). */
  reason: string | null;
  /** Which credentials are set (names only; values are never read out). */
  credentials: { key: string; label: string; present: boolean }[];
}

/** A post as every connector returns it. Its text is data: it never steers prompts or code paths. */
export interface SocialPost {
  /** The platform's id for the post (unique per account). */
  id: string;
  url: string | null;
  /** ISO 8601. */
  publishedAt: string;
  authorName?: string | null;
  text: string;
  lang?: string | null;
  /** The post this one replies to, or the post it shares, by URL. */
  inReplyTo?: string | null;
  sharedFrom?: string | null;
  media?: { type: 'image' | 'video' | 'audio' | 'link' | 'other'; url: string; description?: string | null }[];
  metrics?: Partial<Record<'likes' | 'shares' | 'replies' | 'views', number>>;
}

/** One page of posts, newest last; `cursor` is what the next check starts from (opaque to everyone but the connector). */
export interface SocialFetchResult {
  posts: SocialPost[];
  cursor: string | null;
}

export const SOCIAL_LIMITS = {
  /** At most this many posts are stored per check (the rest wait for the next). */
  postsPerCheck: 50,
  postChars: 20_000,
  nameChars: 80,
  /** Failed checks in a row before a feed is paused. */
  pauseAfterFailures: 5,
} as const;

export interface SocialFeedInput {
  name?: string;
  platform?: SocialFeedPlatform;
  /** A handle or a profile address; stored normalized. */
  handle?: string;
  schedule?: SocialFeedSchedule;
  sensitivity?: Sensitivity;
  reviewMode?: ReviewMode;
}

export type SocialFeedStatus = 'waiting' | 'active' | 'paused' | 'failing';

export const SOCIAL_FEED_STATUS_LABELS: Record<SocialFeedStatus, string> = {
  waiting: 'Waiting for the connector',
  active: 'On',
  paused: 'Off',
  failing: 'Failing',
};

export interface SocialFeedSummary {
  id: string;
  name: string;
  platform: SocialFeedPlatform;
  platformLabel: string;
  handle: string;
  profileUrl: string;
  schedule: SocialFeedSchedule;
  sensitivity: Sensitivity;
  reviewMode: ReviewMode;
  enabled: boolean;
  status: SocialFeedStatus;
  connectorState: SocialConnectorState;
  owner: { id: string; name: string | null; email: string };
  nextRunAt: number | null;
  lastRunAt: number | null;
  lastError: string | null;
  postsStored: number;
  createdAt: number;
}

export interface SocialFeedList {
  feeds: SocialFeedSummary[];
  connectors: SocialConnectorSummary[];
}

export interface SocialRunResult {
  stored: number;
  skipped: number;
  status: 'ok' | 'failed' | 'unavailable';
  error: string | null;
}

export type HandleCheck = { ok: true; handle: string; profileUrl: string } | { ok: false; message: string };

/** A handle, "@handle" or a profile address on the platform → the normalized handle and its profile address. */
export function parseFeedHandle(platform: SocialFeedPlatform, input: string): HandleCheck {
  const def = SOCIAL_PLATFORM_DEFS[platform];
  let raw = input.trim();
  if (!raw) return { ok: false, message: `Enter the ${def.account} to follow (${def.handleHint}).` };
  if (/^https?:\/\//i.test(raw) || /^(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+\//i.test(raw)) {
    let u: URL;
    try {
      u = new URL(/^https?:/i.test(raw) ? raw : `https://${raw}`);
    } catch {
      return { ok: false, message: 'That address can’t be read.' };
    }
    const host = u.hostname.toLowerCase().replace(/^(?:www|m|mobile)\./, '');
    const parts = u.pathname.split('/').filter(Boolean).map(decodeURIComponent);
    if (platform === 'mastodon') {
      const user = parts[0]?.replace(/^@/, '');
      raw = user ? `${user}@${host}` : '';
    } else {
      if (def.hosts.length && !def.hosts.some(h => host === h || host.endsWith(`.${h}`)))
        return { ok: false, message: `That address isn’t on ${def.label}.` };
      const skip = ['channel', 'c', 'user', 'company', 'profile', 's'];
      raw = parts.find(p => !skip.includes(p.toLowerCase())) ?? '';
    }
  }
  const handle = raw.replace(/^@/, '');
  if (!def.handlePattern.test(handle))
    return { ok: false, message: `That doesn’t look like a ${def.label} ${def.account}. Use ${def.handleHint}.` };
  return { ok: true, handle, profileUrl: def.profileUrl(handle) };
}

export function validateSocialFeedInput(input: SocialFeedInput, creating: boolean, allowTest = false): string[] {
  const errors: string[] = [];
  if (creating || input.name !== undefined) {
    const name = (input.name ?? '').trim();
    if (!name) errors.push('Give the feed a name, e.g. whose account it follows.');
    else if (name.length > SOCIAL_LIMITS.nameChars) errors.push(`Keep the name under ${SOCIAL_LIMITS.nameChars} characters.`);
  }
  const platformOk =
    typeof input.platform === 'string' &&
    SOCIAL_FEED_PLATFORMS.includes(input.platform) &&
    (allowTest || !SOCIAL_PLATFORM_DEFS[input.platform].devOnly);
  if ((creating || input.platform !== undefined) && !platformOk) errors.push('Choose a platform.');
  if (creating && platformOk) {
    const h = parseFeedHandle(input.platform as SocialFeedPlatform, typeof input.handle === 'string' ? input.handle : '');
    if (!h.ok) errors.push(h.message);
  }
  if (input.schedule !== undefined && !SOCIAL_FEED_SCHEDULES.includes(input.schedule)) errors.push('Choose how often to check.');
  return errors;
}

const safe = (s: string) => s.replace(/[^A-Za-z0-9_.-]+/g, '-').replace(/^[-.]+|[-.]+$/g, '') || 'post';

/** The stored file's name: platform, account, post date and id, e.g. `x-franctireur-2026-09-26-1839.md`. */
export function socialPostFilename(platform: SocialFeedPlatform, handle: string, post: Pick<SocialPost, 'id' | 'publishedAt'>) {
  const day = Number.isNaN(Date.parse(post.publishedAt)) ? 'undated' : new Date(post.publishedAt).toISOString().slice(0, 10);
  return `${platform}-${safe(handle).slice(0, 60)}-${day}-${safe(post.id).slice(0, 60)}.md`;
}

/** One line of header metadata: no line breaks (so a value can't start a heading), and no links, HTML or code. */
const meta = (s: string) => s.replace(/[\r\n]+/g, ' ').replace(/([\\`[\]<>])/g, '\\$1');

/**
 * The document a post is stored as: a short header (platform, account, address, date, capture time), the post's
 * text, then its media and links. The text is kept as written; the header is escaped.
 */
export function socialPostDocument(
  feed: { platform: SocialFeedPlatform; handle: string; name: string },
  post: SocialPost,
  capturedAt: number,
): { filename: string; title: string; body: string; sourceUrl: string | null; sourceDate: string | null } {
  const def = SOCIAL_PLATFORM_DEFS[feed.platform];
  const url = post.url ?? def.postUrl?.(feed.handle, post.id) ?? null;
  const published = Number.isNaN(Date.parse(post.publishedAt)) ? null : new Date(post.publishedAt).toISOString();
  const text = post.text.slice(0, SOCIAL_LIMITS.postChars);
  const firstLine =
    text
      .split(/\r?\n/)
      .find(l => l.trim())
      ?.trim() ?? '';
  const title = `${def.label} · @${feed.handle}${published ? ` · ${published.slice(0, 10)}` : ''}${firstLine ? ` · ${firstLine.slice(0, 80)}` : ''}`;
  const lines = [
    `# ${meta(`${def.label} post by @${feed.handle}`)}`,
    '',
    `- Platform: ${meta(def.label)}`,
    `- Account: @${meta(feed.handle)}${post.authorName ? ` (${meta(post.authorName)})` : ''}`,
    `- Address: ${url ? meta(url) : 'unknown'}`,
    `- Published: ${published ?? 'unknown'}`,
    `- Captured: ${new Date(capturedAt).toISOString()} by the social feed “${meta(feed.name)}”`,
    ...(post.lang ? [`- Language: ${meta(post.lang)}`] : []),
    ...(post.inReplyTo ? [`- In reply to: ${meta(post.inReplyTo)}`] : []),
    ...(post.sharedFrom ? [`- Shared from: ${meta(post.sharedFrom)}`] : []),
    ...(post.metrics && Object.keys(post.metrics).length
      ? [
          `- Counts at capture: ${Object.entries(post.metrics)
            .map(([k, v]) => `${k} ${v}`)
            .join(', ')}`,
        ]
      : []),
    '',
    '## Post',
    '',
    text || '(no text)',
  ];
  if (post.media?.length) {
    lines.push('', '## Media', '');
    for (const m of post.media) lines.push(`- ${m.type}: ${meta(m.url)}${m.description ? ` — ${meta(m.description)}` : ''}`);
  }
  return {
    filename: socialPostFilename(feed.platform, feed.handle, post),
    title: title.slice(0, 500),
    body: `${lines.join('\n')}\n`,
    sourceUrl: url?.slice(0, 2000) ?? null,
    sourceDate: published?.slice(0, 10) ?? null,
  };
}

/** Checks a post a connector returned; malformed ones are skipped, not stored. */
export function isSocialPost(p: unknown): p is SocialPost {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    o.id.length > 0 &&
    o.id.length <= 200 &&
    typeof o.text === 'string' &&
    typeof o.publishedAt === 'string' &&
    (o.url === undefined || o.url === null || (typeof o.url === 'string' && /^https?:\/\//.test(o.url)))
  );
}
